/* eslint-disable camelcase */
/* eslint-disable max-lines-per-function */
import React from 'react';
import {
    addQueryParamsToUrl,
    classes,
    toReadableDate,
    toReadableDateLong,
} from '../../../lib';
import { DateTime } from 'luxon';
import Styles from './BlogPost.module.less';
import { BackButton } from '../../BackButton/BackButton';
import { SanityObject, imageUrl } from '../../../lib/landingPage';
import { Image, ImageProps } from '../../../bound/landingPage/Image';
import { Logotype } from '../../Logotype';
import { BlogPostPreview } from '../BlogPostPreview';
import _ from 'lodash';
import { Button } from '../../Button';
import routes from '../../../pages/landingPage/routes';
import { LP_BASE_URL } from '../../../config';
import { generatePath } from 'react-router-dom';
import { ShareButtons } from '../ShareButtons';
import { AuthorProps, BlogPostAuthor } from './BlogPostAuthor';
import { CategoryLabel } from './CategoryLabel';
import { FontAwesomeIcon } from '../../FontAwesomeIcon';
import { faClock } from '@fortawesome/pro-regular-svg-icons';

export type RelatedPost = {
    title: string;
    url: string;
    synopsis: string;
    imageRef: SanityObject<ImageProps>;
};

export type BlogPostCategory = {
    slug: string;
    title: string;
    color: string;
    count: number;
};

type BlogPostSection = {
    title: string;
    key: string;
};

type BlogPostSubmenuCTA = {
    text: string;
    url: string;
    buttonText: string;
};

export type BlogPostProps = {
    title: string;
    children: React.ReactNode;
    synopsis: string;
    takeaways?: string[];
    url: string;
    createdAt: DateTime;
    onHomepage: VoidFunction;
    relatedPosts?: RelatedPost[];
    author?: SanityObject<AuthorProps>;
    sections: BlogPostSection[];
    cta?: BlogPostSubmenuCTA;
    category: BlogPostCategory;
    readingTime: number;
};

const RelatedPosts = ({
    relatedPosts,
}: {
    relatedPosts?: RelatedPost[];
}): JSX.Element => {
    if (!relatedPosts || relatedPosts.length === 0) return <></>;
    return (
        <div className="vertical g32">
            <h2>Leia também...</h2>
            <div className={Styles.relatedPostsGrid}>
                {_.uniqBy(relatedPosts, 'url')
                    .slice(0, 3)
                    .map((p, i) => (
                        <BlogPostPreview
                            key={i}
                            title={p.title}
                            small
                            image={imageUrl(p.imageRef.image, 'blog').url()}
                            url={p.url}>
                            {p.synopsis}
                        </BlogPostPreview>
                    ))}
            </div>
        </div>
    );
};

const defaultCta: BlogPostProps['cta'] = {
    text: 'Seja uma imobiliária digital com a Alude. Faça um teste grátis agora mesmo.',
    buttonText: 'TESTE GRÁTIS AGORA',
    url: 'https://app.alude.com.br/',
};

const Sidebar = ({
    sections,
    cta = defaultCta,
    desktopOnly,
    postSlug,
}: {
    sections: BlogPostProps['sections'];
    cta?: BlogPostProps['cta'];
    desktopOnly?: boolean;
    postSlug: string;
}): JSX.Element => {
    const utmParams = {
        utm_source: 'blog',
        utm_campaign: postSlug,
        utm_medium: 'button',
        utm_content: 'sidebar-free-trial-cta',
    };
    return (
        <div className={classes(desktopOnly && 'mt-hidden')}>
            <div className={classes(Styles.sidebar)}>
                <strong>Conteúdo deste post</strong>
                {sections.map(s => (
                    <a
                        key={s.key}
                        href={`#${s.key}`}
                        onClick={e => {
                            e.preventDefault();
                            const el = document.getElementById(s.key);
                            el && el.scrollIntoView({ behavior: 'smooth' });
                        }}>
                        {s.title}
                    </a>
                ))}
                <div className={Styles.sidebarCta}>
                    <strong>{cta.text}</strong>
                    <Button
                        type="primary"
                        block
                        size="large"
                        shape="round"
                        href={addQueryParamsToUrl(cta.url, utmParams)}>
                        {cta?.buttonText}
                    </Button>
                </div>
            </div>
        </div>
    );
};

const Takeaways = ({ takeaways }: { takeaways: string[] }): JSX.Element => {
    return (
        <div className={Styles.takeaways}>
            <h2>💡 Principais conclusões</h2>
            <ol>
                {takeaways.map((t, i) => (
                    <li key={i}>{t}</li>
                ))}
            </ol>
        </div>
    );
};

export const BlogPost = ({
    title,
    synopsis,
    takeaways,
    children,
    createdAt,
    relatedPosts,
    onHomepage,
    author,
    sections,
    cta,
    url,
    category,
    readingTime,
}: BlogPostProps): JSX.Element => {
    return (
        <>
            <div className={Styles.postContainer}>
                <div className="vertical g16 mt-hidden">
                    <ShareButtons
                        url={`${LP_BASE_URL}${generatePath(
                            routes.blog.post.path,
                            {
                                postSlug: url,
                            }
                        )}`}
                        text={title}
                    />
                </div>
                <div className="vertical g16">
                    <div className="vertical g24">
                        <div>
                            <BackButton onClick={onHomepage}>
                                Voltar para o blog
                            </BackButton>
                        </div>
                        <CategoryLabel
                            title={category.title}
                            color={category.color}
                        />
                        <h1>{title}</h1>
                    </div>
                    <div
                        className={classes(
                            Styles.synopsis,
                            'vertical g32 mb32 pb32'
                        )}>
                        <p>{synopsis}</p>
                        {!author ? (
                            <div>
                                Publicado em {toReadableDateLong(createdAt)}
                            </div>
                        ) : (
                            <div className="horizontal left-middle">
                                {author.imageRef ? (
                                    <Image
                                        className={Styles.authorLogo}
                                        value={{
                                            ...author.imageRef,
                                            dataset: 'blog',
                                        }}
                                    />
                                ) : (
                                    <Logotype
                                        type="DefaultIcon"
                                        className={Styles.aludeLogo}
                                    />
                                )}
                                <div
                                    className={classes(
                                        Styles.authorDetails,
                                        'vertical'
                                    )}>
                                    <span>
                                        Escrito por{' '}
                                        <strong>{author.name}</strong>
                                    </span>
                                    <div className="horizontal center-middle g8">
                                        <span>{toReadableDate(createdAt)}</span>
                                        <span>·</span>
                                        <div className="horizontal center-middle g4">
                                            <FontAwesomeIcon icon={faClock} />
                                            {Math.ceil(readingTime)} min de
                                            leitura
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="d-hidden vertical g32">
                        <div className="horizontal g8">
                            <ShareButtons
                                url={`${LP_BASE_URL}${generatePath(
                                    routes.blog.post.path,
                                    {
                                        postSlug: url,
                                    }
                                )}`}
                                text={title}
                            />
                        </div>
                        <Sidebar sections={sections} cta={cta} postSlug={url} />
                    </div>
                    {takeaways && <Takeaways takeaways={takeaways} />}
                    <div className={classes('vertical', Styles.post)}>
                        {children}
                    </div>
                    {author && <BlogPostAuthor author={author} />}
                </div>
                <Sidebar
                    sections={sections}
                    cta={cta}
                    desktopOnly={true}
                    postSlug={url}
                />
            </div>
            <div className="vertical g32">
                <h4>Compartilhe este post</h4>
                <div className="horizontal g16">
                    <ShareButtons
                        url={`${LP_BASE_URL}${generatePath(
                            routes.blog.post.path,
                            {
                                postSlug: url,
                            }
                        )}`}
                        text={title}
                    />
                </div>
            </div>
            <RelatedPosts relatedPosts={relatedPosts} />
        </>
    );
};
